'use client';

/**
 * Dev User Switcher
 *
 * Dropdown to switch between test user personas in development.
 * Loads users from dev_tokens.json and sets the API token.
 *
 * Only renders in development mode.
 *
 * @component_role Dev Tool
 * @layer presentation
 */

import { useEffect, useRef, useState } from 'react';
import { User, ChevronDown, Upload } from 'lucide-react';
import { useDevUserStore, type DevUser } from '@/lib/stores/dev-user-store';
import { parseTokenFile } from '@/lib/auth/parse-token-file';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';

// Dynamic — every loaded persona renders as a simultaneous sibling
// option once the dropdown opens, so it's addressed by its own name,
// not a static key. Own component: useAffordance is a hook, and the
// number of personas varies, so it can't be called per-iteration
// inside the parent's .map() (Rules of Hooks).
function UserOption({ user }: { user: DevUser }) {
  const testId = useAffordance('shell.dev_user_switcher.select_user', { name: user.name });
  return (
    <SelectItem data-testid={testId} value={user.name} className="text-xs">
      <div className="flex flex-col">
        <span className="font-medium">{user.name}</span>
        <span className="text-muted-foreground text-[10px]">{user.role}{user.requires_login ? '' : ' (bypass)'}</span>
      </div>
    </SelectItem>
  );
}

interface DevUserSwitcherProps {
  className?: string;
}

export function DevUserSwitcher({ className }: DevUserSwitcherProps) {
  const { users, selectedUser, isLoaded, loadUsers, selectUser } = useDevUserStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const triggerTestId = useAffordance('shell.dev_user_switcher.trigger');

  // Load users on mount
  useEffect(() => {
    if (!isLoaded) {
      loadUsers();
    }
  }, [isLoaded, loadUsers]);

  const handleChange = (name: string) => {
    const user = users.find((u) => u.name === name);
    selectUser(user || null);
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setFileError(null);
    try {
      const text = await file.text();
      const user = parseTokenFile(text);
      if (!user) {
        setFileError('Could not read a token from that file');
        return;
      }
      await selectUser(user);
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Failed to load token file');
    }
  };

  // Not loaded yet — render nothing rather than a flash of the picker.
  if (!isLoaded) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {users.length > 0 && (
        <>
          <User className="h-4 w-4 text-muted-foreground" />
          <Select value={selectedUser?.name || ''} onValueChange={handleChange}>
            <SelectTrigger data-testid={triggerTestId} className="h-8 w-[180px] text-xs">
              <SelectValue placeholder="Select user..." />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <UserOption key={user.name} user={user} />
              ))}
            </SelectContent>
          </Select>
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.txt,application/json,text/plain"
        onChange={onFileSelected}
        className="hidden"
      />
      <button
        type="button"
        title="Load a token file"
        onClick={() => fileInputRef.current?.click()}
        className="h-8 w-8 flex items-center justify-center rounded-md border border-border hover:bg-accent/50 text-muted-foreground transition-colors"
      >
        <Upload className="h-4 w-4" />
      </button>
      {fileError && <span className="text-[10px] text-destructive">{fileError}</span>}
    </div>
  );
}
